import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Users, Truck } from "lucide-react";

const GEO_URL = "/tr-cities.json";

// Type helpers: her ilin baskın türüne göre renk seçilir
// - Sadece müşteri  → emerald
// - Sadece tedarikçi → rose
// - Karma           → amber
// - Boş             → neutral gri
function pickTone(customer, supplier) {
  if (!customer && !supplier) return "neutral";
  if (customer > 0 && supplier === 0) return "customer";
  if (supplier > 0 && customer === 0) return "supplier";
  return "mixed";
}

const TONE = {
  neutral: { fill: "#f5f5f4", stroke: "#a3a3a3", hover: "#e7e5e4" },
  customer: { fill: "#10b981", stroke: "#065f46", hover: "#059669" },       // emerald-500 → 600
  supplier: { fill: "#e11d48", stroke: "#881337", hover: "#be123c" },       // rose-600 → 700
  mixed:    { fill: "#f59e0b", stroke: "#78350f", hover: "#d97706" },       // amber-500 → 600
};

// Intensity scaling: her ilin toplam sayısına göre opaklık
function intensity(count, max) {
  if (!count) return 0.35;
  const ratio = Math.min(1, count / (max || 1));
  return 0.45 + ratio * 0.55; // 0.45–1.00 arası
}

export default function TurkeyMap() {
  const navigate = useNavigate();
  const [data, setData] = useState([]); // [{province, customer_count, supplier_count, total, districts}]
  const [tooltip, setTooltip] = useState(null); // {x, y, province, cust, supp, total, districts}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/geo-summary");
        if (alive) setData(data || []);
      } catch (e) {
        if (alive) setErr(e?.message || "Yüklenemedi");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const byProvince = useMemo(() => {
    const m = new Map();
    for (const d of data) m.set(d.province, d);
    return m;
  }, [data]);

  const stats = useMemo(() => {
    let totalCust = 0, totalSupp = 0, provincesWith = 0, maxCount = 0;
    for (const d of data) {
      totalCust += d.customer_count || 0;
      totalSupp += d.supplier_count || 0;
      provincesWith += (d.total || 0) > 0 ? 1 : 0;
      maxCount = Math.max(maxCount, d.total || 0);
    }
    return { totalCust, totalSupp, provincesWith, maxCount };
  }, [data]);

  return (
    <div className="brutal-card p-5 relative" data-testid="turkey-map-card">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <p className="label-caps">Coğrafi Dağılım</p>
          <h3 className="font-display text-2xl font-bold mt-1">Türkiye Cari Haritası</h3>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <LegendChip color={TONE.customer.fill} label={`Müşteri (${stats.totalCust})`} icon={<Users size={12} />} />
          <LegendChip color={TONE.supplier.fill} label={`Tedarikçi (${stats.totalSupp})`} icon={<Truck size={12} />} />
          <LegendChip color={TONE.mixed.fill} label="Karma" />
          <span className="text-neutral-500 border-l border-black/10 pl-4">
            {stats.provincesWith} / 81 il aktif
          </span>
        </div>
      </div>

      {loading && (
        <div className="h-96 flex items-center justify-center text-neutral-400 text-sm">Harita yükleniyor…</div>
      )}
      {err && (
        <div className="h-96 flex items-center justify-center text-rose-600 text-sm">Yükleme hatası: {err}</div>
      )}

      {!loading && !err && (
        <div className="relative" onMouseLeave={() => setTooltip(null)}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ center: [35.5, 39.0], scale: 2400 }}
            width={900}
            height={420}
            style={{ width: "100%", height: "auto" }}
            data-testid="turkey-map-svg"
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties?.name || "";
                  const d = byProvince.get(name);
                  const cust = d?.customer_count || 0;
                  const supp = d?.supplier_count || 0;
                  const total = d?.total || 0;
                  const tone = TONE[pickTone(cust, supp)];
                  const alpha = intensity(total, stats.maxCount);
                  const clickable = total > 0;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => {
                        if (!clickable) return;
                        // Sadece bir tür varsa direkt onunla filtrele; karma ise sadece il ile filtrele
                        const params = new URLSearchParams({ province: name });
                        if (cust > 0 && supp === 0) params.set("type", "customer");
                        else if (supp > 0 && cust === 0) params.set("type", "supplier");
                        navigate(`/cari?${params.toString()}`);
                      }}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                          province: name,
                          cust, supp, total,
                          districts: d?.districts || [],
                          clickable,
                        });
                      }}
                      onMouseMove={(e) => {
                        setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : t);
                      }}
                      style={{
                        default: {
                          fill: tone.fill,
                          fillOpacity: alpha,
                          stroke: tone.stroke,
                          strokeWidth: 0.6,
                          outline: "none",
                          transition: "all 0.15s ease-out",
                          cursor: clickable ? "pointer" : "default",
                        },
                        hover: {
                          fill: tone.hover,
                          fillOpacity: 1,
                          stroke: "#000",
                          strokeWidth: 1.2,
                          outline: "none",
                          cursor: clickable ? "pointer" : "default",
                        },
                        pressed: { fill: tone.hover, outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {tooltip && (
            <div
              className="fixed z-50 pointer-events-none bg-white border-2 border-black shadow-lg p-3 min-w-[220px]"
              style={{
                left: Math.min(window.innerWidth - 260, tooltip.x + 14),
                top: Math.max(10, tooltip.y - 12),
              }}
              data-testid="turkey-map-tooltip"
            >
              <div className="flex items-center gap-1 font-display text-base font-bold">
                <MapPin size={14} />
                {tooltip.province}
              </div>
              {tooltip.total > 0 ? (
                <>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    <div>
                      <div className="label-caps text-[9px] text-emerald-700">Müşteri</div>
                      <div className="font-mono-num font-bold">{tooltip.cust}</div>
                    </div>
                    <div>
                      <div className="label-caps text-[9px] text-rose-600">Tedarikçi</div>
                      <div className="font-mono-num font-bold">{tooltip.supp}</div>
                    </div>
                    <div>
                      <div className="label-caps text-[9px]">Toplam</div>
                      <div className="font-mono-num font-bold">{tooltip.total}</div>
                    </div>
                  </div>
                  {tooltip.districts?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-black/10 text-[11px] text-neutral-600">
                      <div className="label-caps text-[9px] mb-1">İlçe Dağılımı</div>
                      {tooltip.districts.slice(0, 5).map((x, i) => (
                        <div key={i} className="flex justify-between">
                          <span>{x.name}</span>
                          <span className="font-mono-num">{x.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {tooltip.clickable && (
                    <div className="mt-2 pt-2 border-t border-black/10 text-[10px] uppercase tracking-widest text-neutral-500">
                      Tıkla → cari listesini aç
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-1 text-xs text-neutral-500">Kayıtlı cari yok</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LegendChip({ color, label, icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="w-3 h-3 border border-black/40" style={{ background: color }} />
      {icon}
      <span>{label}</span>
    </span>
  );
}
